
import { Component, Prop, Vue } from "vue-property-decorator";
import { ParseObject } from "@/plugin/parse/parse.interfaces";
import Loader from "@/components/Loader.vue";
import TituloPrincipal from "@/components/TituloPrincipal.vue";
import Section from "@/components/ui/Section.vue";
import Divider from "@/components/ui/Divider.vue";
import Button from "@/components/ui/Button.vue";
import { IVueFormulateOption } from "@/plugin/utils/utils.interfaces";
import {
  IItemRelatorioFinanceiro,
  IItemCronogramaAtividade,
  IDadosCadastroProjeto,
} from "@/plugin/parse/cloud.interfaces";

const TEMPO_DE_ATUALIZACAO_DO_PROJETO = 120000; // em milissegundos (padrão 2 minutos)

@Component({
  components: {
    Loader,
    TituloPrincipal,
    Section,
    Divider,
    Button,
  },
})
export default class CRUDProjeto extends Vue {
  /**
   * Esse parametro é inicializado pelo componente de visualizar.
   *
   * Quando o usuário clica em editar um projeto, o projeto a ser
   * editado é passado por parametro para esse componente.
   *
   * Caso esse parametro seja undefined, significa que quem está chamando
   * esse componente é a rota para criar um novo projeto.
   */
  @Prop() projetoParaEdicao!: ParseObject;

  /**
   * Esta variável armazenará um novo projeto inicialmente.
   *
   * Caso o componente seja chamado pelo componente de editar um projeto,
   * essa variável será sobrescrita com o projeto a ser editado.
   */
  projeto: ParseObject = this.$parse.newObject("Projeto");

  carregando = true;
  salvando = false;
  enviado = false;
  erro = "";
  errosValidacao: string[] = [];

  intervaloAutoSave: any;

  /**
   * Essas variáveis abaixo são responsáveis por armazenar os as opções disponíveis dentro de um projeto.
   *
   * categorias: A lista de todas as categorias, que serão exibidas no select;
   * areasDeEnfoque: A lista de todas as areasDeEnfoque, que serão exibidas no select;
   * envolvidos: A lista de todas as opções de envolvidos (Rotaractianos, Rotarianos, ...);
   * faixasEtarias: A lista de todas as faixas etárias nos checkboxes de público alvo;
   * dificuldade: A lista de todas as dificuldades, que serão exibidas no select,
   */
  categorias: IVueFormulateOption[] = [];
  areasDeEnfoque: IVueFormulateOption[] = [];
  envolvidos: any = [];
  faixasEtarias: any = [];
  grausDeDificuldade: any = {};

  /**
   * Por algum motivo, ao salvar um projeto, a categoria é atualizada para uma string vazia se apenas passar o valor para o FormulateInput
   *
   * Isso provavelmente é um bug do FormulateInput do tipo 'select'.
   *
   * A solução foi em vez de passar valor, salvar em uma variável separada e usar o v-model no FormulateInput
   */
  categoriaSelecionada: any = null;
  areaDeEnfoqueSelecionada: any = null;

  relatorioFinanceiro = [
    {
      data: "",
      descricao: "",
      valor: 0,
    },
  ];

  cronogramaDeAtividades = [
    {
      data: "",
      atividade: "",
    },
  ];

  emAndamento = true;
  finalizarEdicao = false;
  autoSalvarHabilitado = false;

  dadosProjetoForm: any = {
    titulo: "",
    categoria: "",
    iniciadoEm: "",
  };

  camposPermitidosVazios: string[] = [
    "site",
    "video",
    "idAreaDeEnfoque",
    "finalizadoEm",
  ];

  descricaoCamposObrigatorios: Record<string, string> = {
    titulo: "Título do Projeto",
    idCategoria: "Categoria",
    iniciadoEm: "Data de Início",
    justificativa: "Justificativa",
    objetivoGeral: "Objetivo Geral",
    objetivosEspecificos: "Objetivos Específicos",
    divulgacao: "Meios de Divulgação",
    parcerias: "Parcerias",
    descricao: "Descrição",
    foto: "Link das Fotos",
    resultados: "Resultados Qualitativos e Quantitativos",
    dificuldade: "Grau de dificuldade",
    palavrasChave: "Palavras Chave",
    licoesAprendidas: "Lições Aprendidas",
    resumo: "Resumo",
  };

  async created() {
    this.carregando = true;

    if (this.projetoParaEdicao) {
      this.projeto = this.projetoParaEdicao;
      const relatorioFinanceiro = this.projetoParaEdicao?.get(
        "relatorioFinanceiro"
      );
      if (relatorioFinanceiro && relatorioFinanceiro.length > 0)
        this.relatorioFinanceiro = relatorioFinanceiro;

      const cronogramaDeAtividades = this.projetoParaEdicao?.get("cronograma");
      if (cronogramaDeAtividades && cronogramaDeAtividades.length > 0)
        this.cronogramaDeAtividades = cronogramaDeAtividades;

      this.categoriaSelecionada = this.projetoParaEdicao?.get("categoria")?.id;
      this.areaDeEnfoqueSelecionada =
        this.projetoParaEdicao?.get("areaDeEnfoque")?.id;
    }

    // Parametros já formatados para o FormulateInput
    let parametrosProjetoFormatados = this.$cache.obter(
      "parametros-projeto-formatados"
    );

    if (!parametrosProjetoFormatados) {
      const parametrosProjeto = await this.$cloud.buscarDadosProjeto();
      const {
        categorias,
        areasDeEnfoque,
        envolvidos,
        faixasEtarias,
        grausDeDificuldade,
      } = parametrosProjeto;

      parametrosProjetoFormatados = {
        categorias: this.$utils.converteParaVFOptions(categorias, {
          label: "nome",
          value: "id",
        }),
        areasDeEnfoque: this.$utils.converteParaVFOptions(areasDeEnfoque, {
          label: "nome",
          value: "id",
        }),
        envolvidos: envolvidos.sort(
          (a: ParseObject, b: ParseObject) => a?.get("ID") - b?.get("ID")
        ),
        faixasEtarias: faixasEtarias.sort(
          (a: ParseObject, b: ParseObject) => a?.get("ID") - b?.get("ID")
        ),
        grausDeDificuldade: this.$utils.converteParaVFOptions(
          grausDeDificuldade,
          {
            label: "nome",
            value: "ID",
          }
        ),
      };

      parametrosProjetoFormatados.areasDeEnfoque.unshift({
        id: "sem-area-de-enfoque",
        label: "Selecioar Área de Enfoque",
        value: null,
      });

      this.$cache.salvar(
        "parametros-projeto-formatados",
        parametrosProjetoFormatados
      );
    }

    this.categorias = this.categorias = parametrosProjetoFormatados.categorias;
    this.areasDeEnfoque = parametrosProjetoFormatados.areasDeEnfoque;

    this.envolvidos = parametrosProjetoFormatados.envolvidos;
    this.faixasEtarias = parametrosProjetoFormatados.faixasEtarias;
    this.grausDeDificuldade = parametrosProjetoFormatados.grausDeDificuldade;

    this.carregando = false;
  }

  async salvarProjeto(dadosProjeto: any) {
    if (this.salvando) return;

    // Limpar erros anteriores
    this.errosValidacao = [];

    // Validar campos obrigatórios
    const camposObrigatoriosVazios =
      this.obterCamposObrigatoriosVazios(dadosProjeto);

    if (camposObrigatoriosVazios.length) {
      this.errosValidacao.push(`Preencha os campos obrigatórios: ${camposObrigatoriosVazios.join(
        ", "
      )}.`);
    }

    // Validar relatório financeiro
    const relatorioFinanceiro = this.obterItensFinanceirosValidos(
      dadosProjeto?.relatorioFinanceiro
    );
    if (!relatorioFinanceiro) {
      // A mensagem de erro já foi adicionada ao errosValidacao pelo interromperPorErro
    } else {
      dadosProjeto.relatorioFinanceiro = relatorioFinanceiro;
    }

    // Validar cronograma
    const cronograma = this.obterCronogramaValido(dadosProjeto?.cronograma);
    if (!cronograma) {
      // A mensagem de erro já foi adicionada ao errosValidacao pelo interromperPorErro
    } else {
      dadosProjeto.cronograma = cronograma;
    }

    // Se houver erros, exibir e parar a execução
    if (this.errosValidacao.length > 0) {
      // Fazer scroll até o primeiro campo com erro
      this.scrollParaCampoComErro(dadosProjeto);
      return;
    }

    // Continuar com o salvamento se não houver erros
    this.salvando = true;

    if (dadosProjeto?.envolvidos) {
      const [envolvidos = {}] = dadosProjeto?.envolvidos;
      dadosProjeto.envolvidos = envolvidos;
    }

    if (dadosProjeto?.publicoAlvo) {
      const [publicoAlvo = {}] = dadosProjeto?.publicoAlvo;
      dadosProjeto.publicoAlvo = publicoAlvo;
    }

    // Se o grau de dificuldade estiver em branco
    const dificuldade = Number(dadosProjeto.dificuldade);
    dadosProjeto.dificuldade = Number.isNaN(dificuldade) ? null : dificuldade;

    dadosProjeto.palavrasChave = dadosProjeto.palavrasChave?.split(",") || [];

    const clube = await this.$utils.getMeuClube();

    const dados: IDadosCadastroProjeto = {
      ...dadosProjeto,
      emAndamento: this.emAndamento,
      cadastroFinalizado: !this.emAndamento,
    };

    if (!this.projetoParaEdicao) dados.idClube = clube.id;

    const idProjeto = this.projetoParaEdicao?.id;

    const projetoSalvo = await this.$cloud
      .salvarProjeto({ idProjeto, dados })
      .catch((e) => {
        this.salvando = false;
        this.erro = e.message;
      });

    this.salvando = false;

    if (!this.projetoParaEdicao && projetoSalvo)
      this.$router.push({
        name: "visualizar-projeto",
        params: { id_projeto: projetoSalvo.id },
      });
    this.$emit("edicaoFinalizada", true);
  }

  /**
   * Esse método verifica no intervalo de tempo do TEMPO_DE_ATUALIZACAO_DO_PROJETO (2 min) teve seu progresso já salvo.
   * Caso não tenha sido salvo há mais de dois minutos, salva o projeto automaticamente.
   */
  async salvarProgresso() {
    if (this.salvando) return;

    // console.log("Salvando progresso do projeto");
    this.intervaloAutoSave = setInterval(() => {
      const ultimaAtualizacao: Date = this.projetoParaEdicao.get("updatedAt");

      const progressoParaSalvar =
        ultimaAtualizacao &&
        ultimaAtualizacao.getTime() + TEMPO_DE_ATUALIZACAO_DO_PROJETO <
        new Date().getTime();
      if (!progressoParaSalvar) return;

      // this.salvarProjeto("andamento");
    }, TEMPO_DE_ATUALIZACAO_DO_PROJETO);
  }

  // finalizarEdicaoProjeto(progresso: "andamento" | "finalizado") {
  //   this.finalizarEdicao = true;
  //   // this.salvarProjeto(progresso);
  // }

  /**
   * Esse método é chamado ao clicar em cancelar a edição de um projeto.
   *
   * Se foi clicado na edição de um projeto, finaliza a edição sem salvar as alterações.
   *
   * Se foi clicado no cadastro de um novo projeto, retorna para a URL projetos, excluindo automaticamente o projeto (pois não estava salvo).
   */
  cancelarEdicao() {
    clearInterval(this.intervaloAutoSave);
    if (this.projetoParaEdicao) return this.$emit("edicaoFinalizada", true);
    this.$router.push({ name: "projetos" });
  }

  private obterItensFinanceirosValidos(
    itens: IItemRelatorioFinanceiro[] = []
  ): IItemRelatorioFinanceiro[] | null {
    const lista = itens || [];

    if (!lista.length) {
      this.interromperPorErro(
        "Preencha ao menos um item no Relatório Financeiro."
      );
      return null;
    }

    const completos = lista.filter(
      ({ data, descricao, valor }) =>
        Boolean(data && descricao && (valor || valor === 0 || valor.toString() === "0"))
    );

    if (!completos.length) {
      this.interromperPorErro(
        "Preencha ao menos um item no Relatório Financeiro."
      );
      return null;
    }

    if (completos.length !== lista.length) {
      this.interromperPorErro(
        "Todos os itens do Relatório Financeiro precisam estar completos ou devem ser removidos."
      );
      return null;
    }

    return completos;
  }

  private obterCronogramaValido(
    itens: IItemCronogramaAtividade[] = []
  ): IItemCronogramaAtividade[] | null {
    const lista = itens || [];

    if (!lista.length) {
      this.interromperPorErro(
        "Preencha ao menos uma linha no Cronograma de Atividades."
      );
      return null;
    }

    const completos = lista.filter(
      ({ data, atividade }) => Boolean(data && atividade)
    );

    if (!completos.length) {
      this.interromperPorErro(
        "Preencha ao menos uma linha no Cronograma de Atividades."
      );
      return null;
    }

    if (completos.length !== lista.length) {
      this.interromperPorErro(
        "Todos os itens do Cronograma de Atividades precisam estar completos ou devem ser removidos."
      );
      return null;
    }

    return completos;
  }

  private obterCamposObrigatoriosVazios(
    dadosProjeto: Record<string, unknown>
  ): string[] {
    return Object.keys(this.descricaoCamposObrigatorios)
      .filter((campo) => !this.camposPermitidosVazios.includes(campo))
      .filter((campo) => this.valorEstaVazio(dadosProjeto[campo]))
      .map(
        (campo) => this.descricaoCamposObrigatorios[campo] || campo
      );
  }

  private valorEstaVazio(valor: unknown): boolean {
    if (typeof valor === "string") return valor.trim().length === 0;
    if (Array.isArray(valor)) return valor.length === 0;
    return valor === null || valor === undefined;
  }

  private interromperPorErro(mensagem: string) {
    this.errosValidacao.push(mensagem);
  }

  prepararSalvar(emAndamento: boolean) {
    this.emAndamento = emAndamento;

    // Em caso de erro de validação do Formulate (quando @submit não dispara),
    // tenta rolar para o primeiro erro visível após o ciclo de renderização.
    setTimeout(() => {
      this.scrollParaPrimeiroErroVisual();
    }, 120);
  }

  private scrollParaCampoComErro(dadosProjeto: Record<string, unknown>) {
    // Encontrar o primeiro campo obrigatório vazio
    const camposVazios = this.obterCamposObrigatoriosVazios(dadosProjeto);

    if (camposVazios.length > 0) {
      // Encontrar a chave do campo pela descrição
      const campoComErro = Object.entries(this.descricaoCamposObrigatorios)
        .find(([, desc]) => desc === camposVazios[0])?.[0];

      if (campoComErro) {
        this.$nextTick(() => {
          const input = document.querySelector(
            `input[name="${campoComErro}"], select[name="${campoComErro}"], textarea[name="${campoComErro}"]`
          ) as HTMLElement | null;

          if (input) {
            input.scrollIntoView({ behavior: "smooth", block: "center" });
            input.focus();
            return;
          }

          this.scrollParaPrimeiroErroVisual();
        });
      }
      return;
    }

    this.scrollParaPrimeiroErroVisual();
  }

  private scrollParaPrimeiroErroVisual() {
    const primeiroErro = document.querySelector(
      ".formulate-input--has-errors, .formulate-input-error, .formulate-error, .invalid-feedback"
    ) as HTMLElement | null;

    if (!primeiroErro) return;

    const alvo =
      (primeiroErro.closest(".formulate-input") as HTMLElement | null) ||
      primeiroErro;

    alvo.scrollIntoView({ behavior: "smooth", block: "center" });

    const focavel = alvo.querySelector(
      "input, select, textarea, button"
    ) as HTMLElement | null;
    focavel?.focus();
  }
}
